import { useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Heart, Share2, Quote as QuoteIcon } from "lucide-react";

// Fond premium déterministe (dégradé or/noir) quand aucune photo n'est fournie.
const GRADIENTS = [
  "radial-gradient(120% 120% at 20% 10%, #2a2410 0%, #0a0a0a 55%)",
  "radial-gradient(120% 120% at 80% 20%, #24201a 0%, #080808 60%)",
  "radial-gradient(120% 120% at 50% 90%, #201a12 0%, #0a0a0a 55%)",
  "linear-gradient(135deg, #14100a 0%, #0a0a0a 60%, #1a1508 100%)",
];

export function QuoteCard({ quote, compact = false, animate = false }) {
  const g = GRADIENTS[(quote.author?.length || 0) % GRADIENTS.length];
  return (
    <div data-testid="quote-card"
      className={`relative overflow-hidden rounded-3xl border border-gold/20 ${compact ? "aspect-[16/10]" : "aspect-[4/5] sm:aspect-[16/10]"}`}
      style={{ background: g }}>
      {quote.image_url && (
        <img src={quote.image_url} alt={quote.author} className="absolute inset-0 w-full h-full object-cover" />
      )}
      {/* voile sombre pour lisibilité */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.85) 100%)" }} />
      {/* grain subtil */}
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(rgba(212,175,55,0.6) 0.5px, transparent 0.5px)", backgroundSize: "10px 10px" }} />

      <div className="relative h-full flex flex-col justify-between p-6 sm:p-10">
        <QuoteIcon size={compact ? 28 : 40} className="text-gold/70" />
        <div>
          <p className={`font-display italic tracking-tight text-white leading-snug ${compact ? "text-lg sm:text-2xl" : "text-2xl sm:text-4xl"}`}>
            {animate ? (
              <>
                «{" "}
                {quote.text.split(" ").map((w, i) => (
                  <motion.span key={i} className="inline-block mr-[0.25em]"
                    initial={{ opacity: 0, y: 8, filter: "blur(4px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ delay: 0.6 + i * 0.055, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                    {w}
                  </motion.span>
                ))}
                »
              </>
            ) : (
              <>« {quote.text} »</>
            )}
          </p>
          <div className="mt-5 flex items-end justify-between">
            <div>
              <p className="gold-text font-medium text-base sm:text-lg" data-testid="quote-author">{quote.author}</p>
              {quote.theme && <p className="text-white/40 text-xs uppercase tracking-[0.2em] mt-0.5">{quote.theme}</p>}
            </div>
            <span className="text-white/30 text-xs font-display tracking-wide">BIZARRE <span className="gold-text">AI</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function QuoteActions({ quote, onLikeChange }) {
  const [liked, setLiked] = useState(!!quote.liked);
  const [busy, setBusy] = useState(false);

  const toggleLike = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quote.quote_id}/like`);
      setLiked(data.liked);
      onLikeChange?.(data.liked);
      toast.success(data.liked ? "Ajoutée à vos favoris ❤️" : "Retirée de vos favoris");
    } catch { toast.error("Action impossible."); } finally { setBusy(false); }
  };

  const shareText = `« ${quote.text} » — ${quote.author}\n\nvia BIZARRE AI`;
  const shareUrl = `${window.location.origin}/citation`;

  const doShare = async () => {
    api.post(`/quotes/${quote.quote_id}/share`).catch(() => {});
    if (navigator.share) {
      try { await navigator.share({ title: "Citation du jour — BIZARRE AI", text: shareText, url: shareUrl }); return; }
      catch { /* annulé */ }
    }
    navigator.clipboard?.writeText(`${shareText}\n${shareUrl}`);
    toast.success("Citation copiée — collez-la où vous voulez ✨");
  };

  const net = (url) => { api.post(`/quotes/${quote.quote_id}/share`).catch(() => {}); window.open(url, "_blank"); };
  const enc = encodeURIComponent(`${shareText}\n${shareUrl}`);

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="quote-actions">
      <button data-testid="quote-like-btn" onClick={toggleLike} disabled={busy}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${liked ? "border-red-400/50 text-red-300 bg-red-500/10" : "border-white/15 text-white/70 hover:text-white"}`}>
        <Heart size={15} className={liked ? "fill-red-400 text-red-400" : ""} /> {liked ? "Aimée" : "J'aime"}
      </button>
      <button data-testid="quote-share-btn" onClick={doShare}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm gold-gradient text-black font-medium">
        <Share2 size={15} /> Partager
      </button>
      <button onClick={() => net(`https://wa.me/?text=${enc}`)} className="rounded-full border border-white/15 px-3 py-2 text-sm hover:text-gold" title="WhatsApp">WhatsApp</button>
      <button onClick={() => net(`https://twitter.com/intent/tweet?text=${enc}`)} className="rounded-full border border-white/15 px-3 py-2 text-sm hover:text-gold" title="X">X</button>
      <button onClick={() => net(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`)} className="rounded-full border border-white/15 px-3 py-2 text-sm hover:text-gold" title="Facebook">Facebook</button>
    </div>
  );
}
